import type { AxiosResponse } from "axios";

import { getData, postData } from "@/apis/apiInstance";

import {
  AddTicketCommentPayloadType,
  CreateTicketPayloadType,
  ForwardTicketPayloadType,
  GetTicketAssigedToUserCount,
  GetTicketCategoryWiseCount,
  GetTicketCommentsPayloadType,
  GetTicketDetailsPayloadType,
  GetTicketListPayloadType,
  UpdateTicketPayloadType,
  UpdateTicketStatusPayloadType,
} from "@/commonTypes/supportTypes";

import { getCSRFToken } from "@/utils/commonFunctions";

export async function createTicketApi(
  data: CreateTicketPayloadType
): Promise<AxiosResponse> {
  const { token, ...finalData } = data;

  return await postData(`/finac-crm/support/ticket/create`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function forwardTicketApi(
  data: ForwardTicketPayloadType
): Promise<AxiosResponse> {
  const { token, ...finalData } = data;

  return await postData(`/finac-crm/support/ticket/forward`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function updateTicketApi(
  data: UpdateTicketPayloadType
): Promise<AxiosResponse> {
  const { token, ...finalData } = data;

  return await postData(`/finac-crm/support/ticket/update`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function updateTicketStatusApi(
  data: UpdateTicketStatusPayloadType
): Promise<AxiosResponse> {
  const { token, ...finalData } = data;

  return await postData(`/finac-crm/support/ticket/status/update`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function addTicketCommentApi(
  data: AddTicketCommentPayloadType
): Promise<AxiosResponse> {
  const { token, ...finalData } = data;

  return await postData(`/finac-crm/support/ticket/comment/add`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getTicketDetailsApi(
  data: GetTicketDetailsPayloadType
): Promise<AxiosResponse> {
  const { token, ticket_id, isPageSeen, notificationId, isAdminView } = data;

  return await getData(`/finac-crm/support/ticket/details`, {
    params: {
      ticket_id,
      is_page_seen: isPageSeen,
      notification_id: notificationId,
      is_admin_view: isAdminView,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getTicketCommentsApi(
  data: GetTicketCommentsPayloadType
): Promise<AxiosResponse> {
  const { token, ticket_id } = data;

  return await getData(`/finac-crm/support/ticket/comment/list`, {
    params: { ticket_id },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getTicketListApi(
  data: GetTicketListPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    listing_type,
    limit,
    offset,
    searchTerm,
    priority,
    status,
    isConfidential,
    category_ids,
    sub_category_ids,
    sort_by,
    sorting_order,
    user_id,
    assigned_to,
  } = data;

  return await getData(`/finac-crm/support/ticket/list`, {
    params: {
      listing_type,
      limit,
      offset,
      search_term: searchTerm,
      priority,
      status,
      ...(isConfidential && { is_confidential: isConfidential }),
      category_ids,
      sub_category_ids,
      sort_by,
      sorting_order,
      user_id,
      assigned_to,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getTicketCategoryWiseCount(
  data: GetTicketCategoryWiseCount
): Promise<AxiosResponse> {
  const {
    token,
    category_id,
    priority,
    isConfidential,
    sub_category_id,
    status,
    assigned_to,
    listing_type,
  } = data;

  return await getData(`finac-crm/support/ticket/category/count`, {
    params: {
      category_id,
      priority,
      isConfidential,
      sub_category_id,
      status,
      assigned_to,
      listing_type,
      ...(isConfidential && { is_confidential: isConfidential }),
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getTicketAssignToUserCount(
  data: GetTicketAssigedToUserCount
): Promise<AxiosResponse> {
  const {
    token,
    assigned_to,
    priority,
    isConfidential,
    limit,
    offset,
    status,
    listing_type,
  } = data;

  return await getData(`finac-crm/support/ticket/assigned/user/count`, {
    params: {
      assigned_to,
      priority,
      limit,
      offset,
      status,
      listing_type,
      ...(isConfidential && { is_confidential: isConfidential }),
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}
